import { Component, OnInit, Input, SimpleChanges, OnChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogWaComponent } from 'src/app/features/dialog/dialog-whatsapp/dialog-wa.component';
import dayjs from 'dayjs';
import { SmsService } from 'src/app/core/services/sms/sms.service';
import { SMS_CHARS } from 'src/app/core/utility/conversion';
import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';
import {
	MontyProposalStatus,
	SmsConversationType,
	SmsDirection,
	SmsRepeat,
	SmsType,
	WhatsappProvider,
	WhatsappSettings,
	WhatsappWindowStatesGQL,
} from 'src/app/core/graphql/generated/gen-types';
import { BookingService } from 'src/app/core/services/booking/booking.service';
import { CrewService } from 'src/app/core/services/crew/crew.service';
import { MontyService } from 'src/app/core/services/monty/monty.service';
import { Subscription, skip } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-sms-by-contact',
	templateUrl: './sms-by-contact.component.html',
	styleUrls: ['./sms-by-contact.component.scss'],
})
export class SMSByContactComponent implements OnInit, OnChanges, OnDestroy {
	@Input() conversations: any[];
	@Input() screen: string;
	@Output() listViewChanged = new EventEmitter<boolean>();

	selectedConversation: {
		smsType: SmsType;
		id: string;
		name: string;
		phone: string;
		smsUnread: number;
		smsLatest: { direction: SmsDirection };
		language?: string;
	};
	selectedBooking: any;
	selectedCrew: any;
	// id + latest-message fingerprint of conversations[0]; guards the refetch in ngOnChanges (#165)
	private firstConversationKey: string;
	private bookingDetailSub: Subscription;
	messages: any[] = [];
	listView = true;
	replyText: String;
	smsUp = false;
	smsFlightOptions = false;
	upEnabled = false;
	whatsapp: WhatsappSettings;
	whatsappConnected: boolean | null = null;
	whatsappStatusLoaded: boolean = false;
	hasSmsBalance: boolean = false;
	// 2026-09-02 (US7): cloud tenants only — 24h customer-service window for the selected contact.
	// null = container tenant / not yet checked (no warning shown); false = closed, free text is
	// blocked (no template to fall back to — decideChannel returns 'blocked', FR-006b for freeform).
	// The state is LIVE (US7-AS5): re-checked when a new inbound streams into the thread, and a
	// timer flips it when the known openUntil expires while the conversation stays open.
	windowOpen: boolean | null = null;
	private windowExpiryTimer: ReturnType<typeof setTimeout> | null = null;
	private lastInboundStamp = 0;
	// whether the opening snapshot of the selected thread has been handled — the "initial load"
	// marker used to be `lastInboundStamp === 0`, which mistook the FIRST EVER inbound of a
	// thread for the history load and then skipped the window re-check (bug 2026-09-08)
	private threadLoaded = false;

	// Monty (088): open proposal state for the selected booking conversation.
	// Streams in via the message onSnapshot — no extra queries.
	tenantId: string;
	montyProposal: any = null;
	montyProposalPath: string | null = null;
	montyPrefilled = false;
	montyInsertAvailable = false;
	montyLastDraft = '';
	montyActionBusy = false;
	montyActionFailure: string | null = null;
	// Path whose reply proposal was just sent/refused locally: suppresses
	// re-prefill until the backend resolution streams back (race guard).
	montySuppressedPath: string | null = null;

	constructor(
		private router: Router,
		private smsService: SmsService,
		private bookingService: BookingService,
		private crewService: CrewService,
		private tenantSettingService: TenantSettingService,
		private montyService: MontyService,
		private whatsappWindowStatesGQL: WhatsappWindowStatesGQL,
		private dialog: MatDialog,
		private translate: TranslateService,
	) {}

	ngOnInit() {
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((result) => {
			this.tenantId = result.data.tenantSetting.tenantId;
			if (result.data.tenantSetting.upEnabled) {
				this.upEnabled = true;
			}
			if (result.data.tenantSetting.whatsapp) {
				this.whatsapp = result.data.tenantSetting.whatsapp;
			}
			const smsSubscription = result.data.tenantSetting.subscription?.sms;
			this.hasSmsBalance = smsSubscription?.available > 0 && smsSubscription?.pctUsed < 100;
		});
		if (this.screen === 'booking' || this.screen === 'crew') {
			this.selectedBooking = this.conversations[0];
		}

		this.smsService.getWaConnected()?.pipe(skip(1)).subscribe((connected) => {
			this.whatsappConnected = connected;
			this.whatsappStatusLoaded = true;
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.conversations) {
			this.conversations = changes.conversations.currentValue ?? [];
			// The conversations input re-emits (new array reference) on every Firestore snapshot,
			// even when nothing relevant changed — only refetch when the first conversation
			// actually changed (other id, or a newer latest message), otherwise every booking
			// write anywhere turned into a getBooking network call (#165)
			const first = this.conversations[0];
			const firstKey = first ? `${first.id}|${first.smsLatest?.date?.getTime?.() ?? ''}` : '';
			const firstChanged = firstKey !== this.firstConversationKey;
			this.firstConversationKey = firstKey;
			if (this.screen === 'booking' && first) {
				this.selectedBooking = first;
				if (firstChanged) this.bookingService.getBooking(first.id).refetch();
			}
			if (this.screen === 'crew' && first) {
				this.selectedCrew = first;
				if (firstChanged) this.crewService.get(first.id).refetch();
			}
		}
		if (changes.screen) {
			this.screen = changes.screen.currentValue;
			if (this.screen === 'booking' || this.screen === 'crew') {
				this.selectedBooking = this.conversations[0];
			}
		}
	}

	ngOnDestroy() {
		this.bookingDetailSub?.unsubscribe();
	}

	totalUnread() {
		if (!this.conversations) return;
		return this.conversations.reduce((sum, x) => (sum += x?.smsUnread ?? 0), 0);
	}

	formatDate(date: any) {
		if (!date) return;
		const today = dayjs();
		const dateToFormat = dayjs(date);
		let friendlyDate = dateToFormat.format('dd DD-MMM-YY');
		if (dateToFormat.isSame(today, 'days')) {
			friendlyDate = dateToFormat.format('HH:mm:ss');
		}
		return friendlyDate;
	}

	showSMSList(conversation: any) {
		// unssubscribe from previous conversation
		if (this.selectedConversation) {
			this.smsService.unsubscribeSmsByBooking(this.selectedConversation.id);
		}
		// mark first all messages as read
		this.markSMSesAsRead(conversation);
		this.selectedConversation = null;
		this.listView = true;
		this.listViewChanged.emit(this.listView);
		// an untouched Monty prefill must never leak into the next conversation
		if (this.montyPrefilled && this.replyText === this.montyLastDraft) {
			this.replyText = '';
		}
		this.resetMontyState(true);
	}

	markSMSesAsRead(conversation) {
		if (conversation.smsType === SmsConversationType.Boo) {
			this.smsService.markBookingMessagesRead([conversation.id]).subscribe();
		}
		if (conversation.smsType === SmsConversationType.Cre) {
			this.smsService.markCrewMessagesRead([conversation.id]).subscribe();
		}
	}

	markSMSesAsReadAll(conversations: any) {
		const bookingIds = conversations
			.filter((c) => c.smsType === SmsConversationType.Boo && c.smsUnread)
			.map((c) => c.id);
		const crewIds = conversations
			.filter((c) => c.smsType === SmsConversationType.Cre && c.smsUnread)
			.map((c) => c.id);

		if (bookingIds.length > 0) {
			this.smsService.markBookingMessagesRead(bookingIds).subscribe();
		}
		if (crewIds.length > 0) {
			this.smsService.markCrewMessagesRead(crewIds).subscribe();
		}
	}

	showSelectedSMS(conversation) {
		this.selectedConversation = {
			smsType: conversation.smsType,
			id: conversation.id,
			name: '',
			phone: '',
			smsUnread: conversation.smsUnread,
			smsLatest: conversation.smsLatest,
		};
		this.windowOpen = null;
		this.lastInboundStamp = 0;
		this.threadLoaded = false;
		if (conversation.smsType === SmsConversationType.Boo) {
			this.selectedConversation.name = conversation.contactName;
			this.selectedConversation.phone = conversation.contactPhone;
			this.selectedConversation.language = conversation.contactLanguage;
			// one live watcher at a time — these accumulated per conversation click (#165)
			this.bookingDetailSub?.unsubscribe();
			this.bookingDetailSub = this.bookingService.getBooking(this.selectedConversation.id).valueChanges.subscribe((result) => {
				this.selectedBooking = result.data.booking;
			});
			this.smsService.smsByBooking(this.selectedConversation.id).subscribe((messages) => {
				this.messages = messages;
				this.updateMontyProposal();
				this.onThreadUpdated();
			});
		}
		if (conversation.smsType === SmsConversationType.Cre) {
			this.selectedConversation.name = conversation.name;
			this.selectedConversation.phone = conversation.phone;
			this.selectedConversation.language = conversation.language;
			this.smsService.smsByCrew(this.selectedConversation.id).subscribe((messages) => {
				this.messages = messages;
				this.onThreadUpdated();
			});
		}
		this.checkWindow(this.selectedConversation.phone);
		this.listView = false;
		this.listViewChanged.emit(this.listView);
	}

	smsReply(conversation) {
		// mark first all smses as read
		this.markSMSesAsRead(conversation);
		if (conversation.smsType === SmsConversationType.Boo) {
			this.smsService
				.sendBookingSMS(conversation.id, {
					text: this.replyText,
					up: this.smsUp,
					repeat: SmsRepeat.Single,
					montyProposalPath: this.montyReplyOpen ? this.montyProposalPath : null,
				})
				.subscribe();
			if (this.montyReplyOpen) {
				this.montySuppressedPath = this.montyProposalPath;
			}
			this.resetMontyState(false);
		}
		if (conversation.smsType === SmsConversationType.Cre) {
			this.smsService
				.sendCrewSms(conversation.id, {
					text: this.replyText,
					up: this.smsUp,
					repeat: SmsRepeat.Single,
				})
				.subscribe();
		}
	}

	// 2026-09-02 (US7): cloud tenants only — free text has no template to fall back to when the 24h
	// window is closed, so the send button is blocked; the compose box shows why instead.
	// The backend is the authority; fetchPolicy network-only so a re-check is never cache-served.
	private checkWindow(phone: string) {
		this.clearWindowExpiryTimer();
		this.windowOpen = null;
		if (!phone || this.whatsapp?.provider !== WhatsappProvider.Cloud) return;
		this.whatsappWindowStatesGQL.fetch({ phones: [phone] }, { fetchPolicy: 'network-only' }).subscribe({
			next: (result) => {
				if (phone !== this.selectedConversation?.phone) return; // conversation switched meanwhile
				const state = result.data?.whatsappWindowStates?.[0];
				this.windowOpen = state ? state.open : null;
				// US7-AS5: flip to closed the moment the known window end passes, while the
				// conversation stays open — re-checked against the backend, not assumed.
				if (state?.open && state.openUntil) {
					const delay = new Date(state.openUntil).getTime() - Date.now() + 2000;
					if (delay > 0) {
						this.windowExpiryTimer = setTimeout(() => this.checkWindow(phone), delay);
					}
				}
			},
			error: () => {
				this.windowOpen = null;
			},
		});
	}

	private clearWindowExpiryTimer() {
		if (this.windowExpiryTimer) {
			clearTimeout(this.windowExpiryTimer);
			this.windowExpiryTimer = null;
		}
	}

	// US7-AS5: an inbound message (re)opens the window — the thread already streams via onSnapshot,
	// so a new inbound is the trigger to re-ask the backend for the window state.
	private onThreadUpdated() {
		if (this.whatsapp?.provider !== WhatsappProvider.Cloud || !this.selectedConversation?.phone) return;
		const latestInbound = (this.messages || [])
			.filter((message) => message?.direction === 'IN')
			.reduce((max, message) => Math.max(max, message?.date?.sms?.getTime?.() ?? 0), 0);
		const first = !this.threadLoaded;
		this.threadLoaded = true;
		if (latestInbound > this.lastInboundStamp) {
			this.lastInboundStamp = latestInbound;
			// the initial snapshot delivers the whole history — checkWindow already ran for it
			if (!first) this.checkWindow(this.selectedConversation.phone);
		}
	}

	get windowClosedForFreeform(): boolean {
		return this.whatsapp?.provider === WhatsappProvider.Cloud && this.windowOpen === false;
	}

	// 2026-09-02: a template send must be reachable at all times, not only when the window happens
	// to be closed — proactively sending a template while still in-window is a valid choice too.
	// Hidden once the window is closed: the locked-composer panel below carries its own CTA then,
	// so the header would otherwise show the same action twice (2026-09-02 declutter pass).
	get showTemplateButton(): boolean {
		return this.whatsapp?.provider === WhatsappProvider.Cloud && !!this.selectedConversation && !this.windowClosedForFreeform;
	}

	// 2026-09-03 (US7 review): the template send IS the standard send dialog (dialog-wa, the same
	// popup a reminder uses) in conversation mode — type selectable inside, one recipient.
	openTemplatePicker() {
		if (!this.selectedConversation) return;
		const conversationType = this.selectedConversation.smsType as any as SmsConversationType;
		this.dialog
			.open(DialogWaComponent, {
				// 2026-09-08: the type selector needs the room — the popup was too narrow for it
				width: '760px',
				maxWidth: '95vw',
				maxHeight: '85vh',
				data: {
					conversation: {
						type: conversationType,
						id: this.selectedConversation.id,
						phone: this.selectedConversation.phone,
						language: this.selectedConversation.language || 'nl',
						name: this.selectedConversation.name,
					},
					flight: conversationType === SmsConversationType.Boo ? this.selectedBooking?.flight || null : null,
					// 2026-09-12: the reservation confirmation renders booking-only variables
					booking: conversationType === SmsConversationType.Boo ? this.selectedBooking || null : null,
					whatsapp: this.whatsapp,
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result === 'sent') this.markSMSesAsRead(this.selectedConversation);
			});
	}

	// US7-AS6: the backend is the authority on the window — a WHATSAPP_WINDOW_CLOSED rejection
	// (stale UI check, window expired mid-composition) flips the composer to the closed panel.
	// Nothing was written server-side, and replyText is untouched, so the operator keeps the text.
	private handleFreeformSendError(error: any) {
		const closed = (error?.graphQLErrors || []).some(
			(gqlError: any) => gqlError?.extensions?.code === 'WHATSAPP_WINDOW_CLOSED' || String(gqlError?.message || '').includes('WHATSAPP_WINDOW_CLOSED'),
		);
		if (closed) {
			this.windowOpen = false;
		} else {
			console.error('[sms-by-contact] freeform send failed:', error);
		}
	}

	messageReply(conversation) {
		if (this.windowClosedForFreeform) return;
		// mark first all smses as read
		this.markSMSesAsRead(conversation);
		if (conversation.smsType === SmsConversationType.Boo) {
			this.smsService
				.sendBookingMessage(conversation.id, {
					text: this.replyText,
					up: this.smsUp,
					flightOptions: this.smsFlightOptions,
					repeat: SmsRepeat.Single,
					montyProposalPath: this.montyReplyOpen ? this.montyProposalPath : null,
				})
				.subscribe({ error: (error) => this.handleFreeformSendError(error) });
			if (this.montyReplyOpen) {
				this.montySuppressedPath = this.montyProposalPath;
			}
			this.resetMontyState(false);
		}
		if (conversation.smsType === SmsConversationType.Cre) {
			this.smsService
				.sendCrewMessage(conversation.id, {
					text: this.replyText,
				})
				.subscribe({ error: (error) => this.handleFreeformSendError(error) });
		}
	}

	characterCount(text: string) {
		if (text) {
			return SMS_CHARS - (text.length - (this.smsCount(text) - 1) * SMS_CHARS);
		}
		return 0;
	}

	smsCount(text: string) {
		if (text) {
			return Math.floor((text.length - 1) / SMS_CHARS) + 1;
		}
		return 0;
	}

	viewDetailsBooking(conversation) {
		this.navigateToBooking(conversation.id);
	}

	viewDetailsFlight(conversation) {
		this.navigateToFlight(conversation.flight?.id);
	}

	navigateToBooking(id?) {
		let url = '/booking';
		if (id) {
			url = url + '/' + id;
		}
		this.router.navigate([url]);
	}

	navigateToFlight(id?) {
		let url = '/flight';
		if (id) {
			url = url + '/' + id;
		}
		this.router.navigate([url]);
	}

	// ── Monty (088) ─────────────────────────────────────────────────────────

	get montyReplyOpen(): boolean {
		return this.montyProposal?.status === 'OPEN';
	}

	/** 224: the proposal carries checked facts worth showing (requested dates / review flags) */
	get montyFacts(): boolean {
		const p = this.montyProposal;
		return !!p && (!!p.requested?.length || !!p.needsReview || !!p.dateConflict);
	}

	/** 224: "YYYY-MM-DD" → "za 24/10" in the operator's language */
	montyDay(day: string): string {
		return dayjs(day).locale(this.translate.currentLang || 'nl').format('dd D/MM');
	}

	get montyActionPending(): boolean {
		const action = this.montyProposal?.action;
		return !!action && !action.accepted && !action.dismissed && this.montyProposal?.status !== 'EXPIRED';
	}

	/** Recompute the open proposal from the streamed messages (booking only). */
	updateMontyProposal() {
		if ((this.selectedConversation?.smsType as any) !== SmsConversationType.Boo) return;
		const proposalMessage = (this.messages || []).find((message) => {
			const proposal = message?.monty?.proposal;
			if (!proposal) return false;
			const actionPending = proposal.action && !proposal.action.accepted && !proposal.action.dismissed;
			return proposal.status === 'OPEN' || (actionPending && proposal.status !== 'EXPIRED');
		});
		const proposal = proposalMessage?.monty?.proposal || null;
		const previousPath = this.montyProposalPath;
		this.montyProposal = proposal;
		this.montyProposalPath = proposalMessage
			? `tenants/${this.tenantId}/booking/${this.selectedConversation.id}/message/${proposalMessage._docId}`
			: null;
		if (this.montyProposalPath !== previousPath) {
			// a new/other proposal — stale failure text must not carry over
			this.montyActionFailure = null;
		}
		// lift the local suppression once the backend resolution streamed back
		if (this.montySuppressedPath && (this.montyProposalPath !== this.montySuppressedPath || proposal?.status !== 'OPEN')) {
			this.montySuppressedPath = null;
		}
		if (this.montySuppressedPath === this.montyProposalPath && proposal?.status === 'OPEN') {
			// just sent/refused locally; ignore the not-yet-resolved stream state
			return;
		}

		if (proposal?.status === 'OPEN') {
			if (!this.replyText) {
				// composer empty → pre-fill; sending is the accept
				this.replyText = proposal.reply?.text || '';
				this.montyLastDraft = this.replyText as string;
				this.montyPrefilled = true;
				this.montyInsertAvailable = false;
			} else if (!this.montyPrefilled) {
				// operator already typing → never replace, offer insert
				this.montyInsertAvailable = true;
			}
		} else {
			// proposal resolved/expired elsewhere → silently drop the untouched draft
			if (this.montyPrefilled && this.replyText === this.montyLastDraft) {
				this.replyText = '';
			}
			this.montyPrefilled = false;
			this.montyInsertAvailable = false;
		}
	}

	montyInsertDraft() {
		if (!this.montyProposal) return;
		this.replyText = this.montyProposal.reply?.text || '';
		this.montyLastDraft = this.replyText as string;
		this.montyPrefilled = true;
		this.montyInsertAvailable = false;
	}

	montyClearProposal() {
		if (!this.montyProposalPath) return;
		this.montySuppressedPath = this.montyProposalPath;
		this.montyService.resolveProposal(this.montyProposalPath, MontyProposalStatus.Refused).subscribe();
		if (this.montyPrefilled && this.replyText === this.montyLastDraft) {
			this.replyText = '';
		}
		this.resetMontyState(false);
	}

	montyAcceptAction(flightId: string) {
		if (!this.montyProposalPath) return;
		this.montyActionBusy = true;
		this.montyActionFailure = null;
		this.montyService.acceptAction(this.montyProposalPath, flightId).subscribe({
			next: (result) => {
				if (!result?.ok) {
					this.montyActionBusy = false;
					this.montyActionFailure = result?.failureReason || 'PROPOSAL_NOT_OPEN';
				}
				// on success the card stays busy until the accepted stamp
				// streams back and hides it — prevents a double accept window
			},
			error: () => {
				this.montyActionBusy = false;
				this.montyActionFailure = 'PROPOSAL_NOT_OPEN';
			},
		});
	}

	montyDismissAction() {
		if (!this.montyProposalPath) return;
		this.montyService.resolveProposal(this.montyProposalPath, MontyProposalStatus.DismissedAction).subscribe();
	}

	private resetMontyState(clearProposal: boolean) {
		if (clearProposal) {
			this.montyProposal = null;
			this.montyProposalPath = null;
		}
		this.montyPrefilled = false;
		this.montyInsertAvailable = false;
		this.montyActionBusy = false;
		this.montyActionFailure = null;
	}
}
